import { useFormContext, useWatch } from 'react-hook-form';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import FormAutoHeightTextarea from 'components/common/forms/FormAutoHeightTextarea';
import FormCheckbox from 'components/common/forms/FormCheckbox';
import classNames from 'classnames';
import styles from 'styles/pages/chart/MethodsOfOralHygieneChart.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import {
  BrushingTeethRoutine,
  BrushingTeethMethod,
  BrushingTeethMethodLabel,
  MethodsOfOralHygieneChartFields,
  BrushingTeethRoutineLabel,
  BrushingTeethTimeLabel,
  BrushingTeethTime,
  BrushingTeethIntensity,
  BrushingTeethIntensityLabel,
  ToothpasteTypesLabel,
  ToothpasteTypes,
  OralCareProductLabel,
  OralCareProduct,
  DietaryAssessment,
  DietaryAssessmentLabel,
} from 'constants/chart/methodsOfOralHygieneChart';
import useModal from 'hooks/useModal';

type TProps = {
  isRegister: boolean;
  isReadOnly: boolean;
};

const MethodsOfOralHygieneChart = ({ isRegister, isReadOnly }: TProps) => {
  const { openModal } = useModal();

  const { register, setValue, control } = useFormContext();

  const brushingTeethMethodActionWatch = useWatch({
    name: MethodsOfOralHygieneChartFields.BrushingTeethMethodAction,
    control,
  });
  const brushingTeethMethodDetailWatch = useWatch({
    name: MethodsOfOralHygieneChartFields.BrushingTeethMethodDetail,
    control,
  });

  const brushingTeethRoutineActionWatch = useWatch({
    name: MethodsOfOralHygieneChartFields.BrushingTeethRoutineAction,
    control,
  });
  const brushingTeethRoutineDetail1Watch = useWatch({
    name: MethodsOfOralHygieneChartFields.BrushingTeethRoutineDetail1,
    control,
  });
  const brushingTeethRoutineDetail2Watch = useWatch({
    name: MethodsOfOralHygieneChartFields.BrushingTeethRoutineDetail2,
    control,
  });

  const handleRoutineChange = () => {
    if (
      (brushingTeethRoutineActionWatch?.includes(
        BrushingTeethRoutine.routine2
      ) &&
        brushingTeethRoutineDetail1Watch) ||
      (brushingTeethRoutineActionWatch?.includes(
        BrushingTeethRoutine.routine3
      ) &&
        brushingTeethRoutineDetail2Watch)
    ) {
      openModal({
        isConfirm: true,
        title: '작성 중인 내용을 삭제하시겠습니까?',
        text: '작성 중인 내용이 삭제됩니다.',
        onCancel: () => {
          setValue(
            MethodsOfOralHygieneChartFields.BrushingTeethRoutineAction,
            brushingTeethRoutineActionWatch?.includes(
              BrushingTeethRoutine.routine2
            )
              ? BrushingTeethRoutine.routine2
              : BrushingTeethRoutine.routine3
          );
        },
        onSubmit: () => {
          setValue(
            MethodsOfOralHygieneChartFields.BrushingTeethRoutineDetail1,
            ''
          );
          setValue(
            MethodsOfOralHygieneChartFields.BrushingTeethRoutineDetail2,
            ''
          );
        },
      });
    }
  };

  const oralCareProductActionWatch = useWatch({
    name: MethodsOfOralHygieneChartFields.OralCareProductAction,
    control,
  });
  const oralCareProductDetailWatch = useWatch({
    name: MethodsOfOralHygieneChartFields.OralCareProductDetail,
    control,
  });

  const dietaryAssessmentActionWatch = useWatch({
    name: MethodsOfOralHygieneChartFields.DietaryAssessmentAction,
    control,
  });
  const dietaryAssessmentDetail1Watch = useWatch({
    name: MethodsOfOralHygieneChartFields.DietaryAssessmentDetail1,
    control,
  });
  const dietaryAssessmentDetail2Watch = useWatch({
    name: MethodsOfOralHygieneChartFields.DietaryAssessmentDetail2,
    control,
  });
  const dietaryAssessmentDetail3Watch = useWatch({
    name: MethodsOfOralHygieneChartFields.DietaryAssessmentDetail3,
    control,
  });

  const handleDietaryChange = (value: any) => {
    if (
      !value?.includes(DietaryAssessment.dietary1) &&
      dietaryAssessmentDetail1Watch
    ) {
      openModal({
        isConfirm: true,
        title: '작성 중인 내용을 삭제하시겠습니까?',
        text: '작성 중인 내용이 삭제됩니다.',
        onCancel: () => {
          setValue(MethodsOfOralHygieneChartFields.DietaryAssessmentAction, [
            ...value,
            DietaryAssessment.dietary1,
          ]);
        },
        onSubmit: () => {
          setValue(
            MethodsOfOralHygieneChartFields.DietaryAssessmentDetail1,
            ''
          );
        },
      });
    } else if (
      !value?.includes(DietaryAssessment.dietary2) &&
      dietaryAssessmentDetail2Watch
    ) {
      openModal({
        isConfirm: true,
        title: '작성 중인 내용을 삭제하시겠습니까?',
        text: '작성 중인 내용이 삭제됩니다.',
        onCancel: () => {
          setValue(MethodsOfOralHygieneChartFields.DietaryAssessmentAction, [
            ...value,
            DietaryAssessment.dietary2,
          ]);
        },
        onSubmit: () => {
          setValue(
            MethodsOfOralHygieneChartFields.DietaryAssessmentDetail2,
            ''
          );
        },
      });
    } else if (
      !value?.includes(DietaryAssessment.dietary3) &&
      dietaryAssessmentDetail3Watch
    ) {
      openModal({
        isConfirm: true,
        title: '작성 중인 내용을 삭제하시겠습니까?',
        text: '작성 중인 내용이 삭제됩니다.',
        onCancel: () => {
          setValue(MethodsOfOralHygieneChartFields.DietaryAssessmentAction, [
            ...value,
            DietaryAssessment.dietary3,
          ]);
        },
        onSubmit: () => {
          setValue(
            MethodsOfOralHygieneChartFields.DietaryAssessmentDetail3,
            ''
          );
        },
      });
    }
  };

  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr className={styles.thead_tr}>
            <th className={styles.th}></th>
            <th className={styles.th}>구강위생관리방법</th>
            <th className={styles.th}>구강위생관리 행동</th>
            <th className={styles.th}>구강보건교육 계획</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              1
            </td>
            <td className={styles.td}>이 닦기 빈도</td>
            <td className={styles.td}>
              <div className="flex gap16">
                <input
                  {...register(
                    MethodsOfOralHygieneChartFields.BrushingTeethFrequencyAction
                  )}
                  type="number"
                  className={styles.input}
                  placeholder="예) 3, 4"
                  readOnly={!isRegister || isReadOnly}
                />
                <span>/day</span>
              </div>
            </td>
            <td className={styles.textarea_td}>
              <FormAutoHeightTextarea
                name={
                  MethodsOfOralHygieneChartFields.BrushingTeethFrequencyPlan
                }
                textareaStyle={{
                  width: '100%',
                  height: '62px',
                  padding: '16px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
                readOnly={!isRegister || isReadOnly}
              />
            </td>
          </tr>

          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              2
            </td>
            <td className={styles.td}>이 닦기 방법</td>
            <td className={styles.td}>
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.BrushingTeethMethodAction}
                text={BrushingTeethMethodLabel[BrushingTeethMethod.method1]}
                labelStyle={{ marginRight: '24px' }}
                value={BrushingTeethMethod.method1}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.BrushingTeethMethodAction}
                text={BrushingTeethMethodLabel[BrushingTeethMethod.method2]}
                labelStyle={{ marginRight: '24px' }}
                value={BrushingTeethMethod.method2}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.BrushingTeethMethodAction}
                text={BrushingTeethMethodLabel[BrushingTeethMethod.method3]}
                labelStyle={{ marginRight: '24px' }}
                value={BrushingTeethMethod.method3}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.BrushingTeethMethodAction}
                text={BrushingTeethMethodLabel[BrushingTeethMethod.method4]}
                labelStyle={{ marginRight: '24px' }}
                value={BrushingTeethMethod.method4}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.BrushingTeethMethodAction}
                text={BrushingTeethMethodLabel[BrushingTeethMethod.method5]}
                labelStyle={{ marginRight: '24px' }}
                value={BrushingTeethMethod.method5}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <div className="flex">
                <FormCheckbox
                  name={
                    MethodsOfOralHygieneChartFields.BrushingTeethMethodAction
                  }
                  text={BrushingTeethMethodLabel[BrushingTeethMethod.method6]}
                  value={BrushingTeethMethod.method6}
                  multiple={true}
                  readOnly={!isRegister || isReadOnly}
                  afterChange={() => {
                    if (
                      brushingTeethMethodActionWatch?.includes(
                        BrushingTeethMethod.method6
                      ) &&
                      brushingTeethMethodDetailWatch
                    ) {
                      openModal({
                        isConfirm: true,
                        title: '작성 중인 내용을 삭제하시겠습니까?',
                        text: '작성 중인 내용이 삭제됩니다.',
                        onCancel: () => {
                          setValue(
                            MethodsOfOralHygieneChartFields.BrushingTeethMethodAction,
                            [
                              ...brushingTeethMethodActionWatch,
                              BrushingTeethMethod.method6,
                            ]
                          );
                        },
                        onSubmit: () => {
                          setValue(
                            MethodsOfOralHygieneChartFields.BrushingTeethMethodDetail,
                            ''
                          );
                        },
                      });
                    }
                  }}
                />
                <FormAutoHeightTextarea
                  name={
                    MethodsOfOralHygieneChartFields.BrushingTeethMethodDetail
                  }
                  textareaStyle={{ flex: '1' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    !brushingTeethMethodActionWatch.includes(
                      BrushingTeethMethod.method6
                    )
                  }
                />
              </div>
            </td>
            <td className={styles.textarea_td}>
              <FormAutoHeightTextarea
                name={MethodsOfOralHygieneChartFields.BrushingTeethMethodPlan}
                textareaStyle={{
                  width: '100%',
                  height: '112px',
                  padding: '16px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
              />
            </td>
          </tr>

          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              3
            </td>
            <td className={styles.td}>이 닦기 시기</td>
            <td className={styles.td}>
              <div className="flex flex_column gap12">
                <div className="flex">
                  <FormRadioBtn
                    name={
                      MethodsOfOralHygieneChartFields.BrushingTeethRoutineAction
                    }
                    labelText={
                      BrushingTeethRoutineLabel[BrushingTeethRoutine.routine1]
                    }
                    value={BrushingTeethRoutine.routine1}
                    readOnly={!isRegister || isReadOnly}
                    afterChange={handleRoutineChange}
                  />
                </div>
                <div className="flex">
                  <FormRadioBtn
                    name={
                      MethodsOfOralHygieneChartFields.BrushingTeethRoutineAction
                    }
                    labelText={
                      BrushingTeethRoutineLabel[BrushingTeethRoutine.routine2]
                    }
                    value={BrushingTeethRoutine.routine2}
                    radioStyle={{ width: '88px' }}
                    readOnly={!isRegister || isReadOnly}
                    afterChange={handleRoutineChange}
                  />
                  <FormAutoHeightTextarea
                    name={
                      MethodsOfOralHygieneChartFields.BrushingTeethRoutineDetail1
                    }
                    placeholder="최대 30자까지 입력가능합니다."
                    textareaStyle={{ flex: '1' }}
                    maxLength={30}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      !brushingTeethRoutineActionWatch?.includes(
                        BrushingTeethRoutine.routine2
                      )
                    }
                  />
                </div>
                <div className="flex">
                  <FormRadioBtn
                    name={
                      MethodsOfOralHygieneChartFields.BrushingTeethRoutineAction
                    }
                    labelText={
                      BrushingTeethRoutineLabel[BrushingTeethRoutine.routine3]
                    }
                    value={BrushingTeethRoutine.routine3}
                    readOnly={!isRegister || isReadOnly}
                    afterChange={handleRoutineChange}
                  />
                  <FormAutoHeightTextarea
                    name={
                      MethodsOfOralHygieneChartFields.BrushingTeethRoutineDetail2
                    }
                    placeholder="최대 30자까지 입력가능합니다."
                    textareaStyle={{ flex: '1' }}
                    maxLength={30}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      !brushingTeethRoutineActionWatch?.includes(
                        BrushingTeethRoutine.routine3
                      )
                    }
                  />
                </div>
              </div>
            </td>
            <td className={styles.textarea_td}>
              <FormAutoHeightTextarea
                name={MethodsOfOralHygieneChartFields.BrushingTeethRoutinePlan}
                textareaStyle={{
                  width: '100%',
                  height: '128px',
                  padding: '16px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
                readOnly={!isRegister || isReadOnly}
              />
            </td>
          </tr>

          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              4
            </td>
            <td className={styles.td}>이 닦기 시간</td>
            <td className={styles.td}>
              <div className="flex gap24">
                <FormRadioBtn
                  name={MethodsOfOralHygieneChartFields.BrushingTeethTimeAction}
                  labelText={BrushingTeethTimeLabel[BrushingTeethTime.time1]}
                  value={BrushingTeethTime.time1}
                  readOnly={!isRegister || isReadOnly}
                />
                <FormRadioBtn
                  name={MethodsOfOralHygieneChartFields.BrushingTeethTimeAction}
                  labelText={BrushingTeethTimeLabel[BrushingTeethTime.time2]}
                  value={BrushingTeethTime.time2}
                  readOnly={!isRegister || isReadOnly}
                />
                <FormRadioBtn
                  name={MethodsOfOralHygieneChartFields.BrushingTeethTimeAction}
                  labelText={BrushingTeethTimeLabel[BrushingTeethTime.time3]}
                  value={BrushingTeethTime.time3}
                  readOnly={!isRegister || isReadOnly}
                />
              </div>
            </td>
            <td className={styles.textarea_td}>
              <FormAutoHeightTextarea
                name={MethodsOfOralHygieneChartFields.BrushingTeethTimePlan}
                textareaStyle={{
                  width: '100%',
                  height: '62px',
                  padding: '16px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
              />
            </td>
          </tr>

          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              5
            </td>
            <td className={styles.td}>이 닦기 강도</td>
            <td className={styles.td}>
              <div className="flex gap48">
                <FormRadioBtn
                  name={
                    MethodsOfOralHygieneChartFields.BrushingTeethIntensityAction
                  }
                  labelText={
                    BrushingTeethIntensityLabel[BrushingTeethIntensity.soft]
                  }
                  value={BrushingTeethIntensity.soft}
                  readOnly={!isRegister || isReadOnly}
                />
                <FormRadioBtn
                  name={
                    MethodsOfOralHygieneChartFields.BrushingTeethIntensityAction
                  }
                  labelText={
                    BrushingTeethIntensityLabel[BrushingTeethIntensity.hard]
                  }
                  value={BrushingTeethIntensity.hard}
                  readOnly={!isRegister || isReadOnly}
                />
              </div>
            </td>
            <td className={styles.textarea_td}>
              <FormAutoHeightTextarea
                name={
                  MethodsOfOralHygieneChartFields.BrushingTeethIntensityPlan
                }
                textareaStyle={{
                  width: '100%',
                  height: '62px',
                  padding: '16px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
                readOnly={!isRegister || isReadOnly}
              />
            </td>
          </tr>

          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              6
            </td>
            <td className={styles.td}>사용 중인 치약 종류</td>
            <td className={styles.td}>
              <div className="flex gap12">
                <FormRadioBtn
                  name={MethodsOfOralHygieneChartFields.ToothpasteTypesAction}
                  labelText={ToothpasteTypesLabel[ToothpasteTypes.type1]}
                  value={ToothpasteTypes.type1}
                  readOnly={!isRegister || isReadOnly}
                />
                <FormRadioBtn
                  name={MethodsOfOralHygieneChartFields.ToothpasteTypesAction}
                  labelText={ToothpasteTypesLabel[ToothpasteTypes.type2]}
                  value={ToothpasteTypes.type2}
                  readOnly={!isRegister || isReadOnly}
                />
              </div>
            </td>
            <td className={styles.textarea_td}>
              <FormAutoHeightTextarea
                name={MethodsOfOralHygieneChartFields.ToothpasteTypesPlan}
                textareaStyle={{
                  width: '100%',
                  height: '62px',
                  padding: '16px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
                readOnly={!isRegister || isReadOnly}
              />
            </td>
          </tr>

          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              7
            </td>
            <td className={styles.td}>구강관리용품 사용</td>
            <td className={styles.td}>
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                text={OralCareProductLabel[OralCareProduct.product1]}
                labelStyle={{ marginRight: '24px' }}
                value={OralCareProduct.product1}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                text={OralCareProductLabel[OralCareProduct.product2]}
                labelStyle={{ marginRight: '24px' }}
                value={OralCareProduct.product2}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                text={OralCareProductLabel[OralCareProduct.product3]}
                labelStyle={{ marginRight: '24px' }}
                value={OralCareProduct.product3}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                text={OralCareProductLabel[OralCareProduct.product4]}
                labelStyle={{ marginRight: '24px' }}
                value={OralCareProduct.product4}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                text={OralCareProductLabel[OralCareProduct.product5]}
                labelStyle={{ marginRight: '24px' }}
                value={OralCareProduct.product5}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                text={OralCareProductLabel[OralCareProduct.product6]}
                labelStyle={{ marginRight: '24px' }}
                value={OralCareProduct.product6}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                text={OralCareProductLabel[OralCareProduct.product7]}
                labelStyle={{ marginRight: '24px' }}
                value={OralCareProduct.product7}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <div className="flex">
                <FormCheckbox
                  name={MethodsOfOralHygieneChartFields.OralCareProductAction}
                  text={OralCareProductLabel[OralCareProduct.product8]}
                  value={OralCareProduct.product8}
                  multiple={true}
                  readOnly={!isRegister || isReadOnly}
                  afterChange={() => {
                    if (
                      oralCareProductActionWatch?.includes(
                        OralCareProduct.product8
                      ) &&
                      oralCareProductDetailWatch
                    ) {
                      openModal({
                        isConfirm: true,
                        title: '작성 중인 내용을 삭제하시겠습니까?',
                        text: '작성 중인 내용이 삭제됩니다.',
                        onCancel: () => {
                          setValue(
                            MethodsOfOralHygieneChartFields.OralCareProductAction,
                            [
                              ...oralCareProductActionWatch,
                              OralCareProduct.product8,
                            ]
                          );
                        },
                        onSubmit: () => {
                          setValue(
                            MethodsOfOralHygieneChartFields.OralCareProductDetail,
                            ''
                          );
                        },
                      });
                    }
                  }}
                />
                <FormAutoHeightTextarea
                  name={MethodsOfOralHygieneChartFields.OralCareProductDetail}
                  textareaStyle={{
                    borderBottom: `1px solid ${STYLES_VARIABLES.GRAY_03}`,
                    flex: '1',
                  }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    !oralCareProductActionWatch?.includes(
                      OralCareProduct.product8
                    )
                  }
                />
              </div>
            </td>
            <td className={styles.textarea_td}>
              <FormAutoHeightTextarea
                name={MethodsOfOralHygieneChartFields.OralCareProductPlan}
                textareaStyle={{
                  width: '100%',
                  padding: '16px',
                  height: '140px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
                readOnly={!isRegister || isReadOnly}
              />
            </td>
          </tr>

          <tr className={styles.tbody_tr}>
            <td
              className={classNames(styles.td, styles.rounded_bottom_left)}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              8
            </td>
            <td className={styles.td}>식이 평가</td>
            <td className={classNames(styles.td)}>
              <div className="flex flex_column gap12">
                <div className="flex items_center justify_between">
                  <FormCheckbox
                    name={
                      MethodsOfOralHygieneChartFields.DietaryAssessmentAction
                    }
                    text={DietaryAssessmentLabel[DietaryAssessment.dietary1]}
                    value={DietaryAssessment.dietary1}
                    multiple={true}
                    readOnly={!isRegister || isReadOnly}
                    afterChange={handleDietaryChange}
                  />
                  <input
                    {...register(
                      MethodsOfOralHygieneChartFields.DietaryAssessmentDetail1
                    )}
                    type="number"
                    placeholder="예) 3, 4"
                    className={styles.dietary_input}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      !dietaryAssessmentActionWatch?.includes(
                        DietaryAssessment.dietary1
                      )
                    }
                  />
                  <span>회 /day</span>
                </div>
                <div className="flex items_center justify_between">
                  <FormCheckbox
                    name={
                      MethodsOfOralHygieneChartFields.DietaryAssessmentAction
                    }
                    text={DietaryAssessmentLabel[DietaryAssessment.dietary2]}
                    value={DietaryAssessment.dietary2}
                    multiple={true}
                    readOnly={!isRegister || isReadOnly}
                    afterChange={handleDietaryChange}
                  />
                  <input
                    {...register(
                      MethodsOfOralHygieneChartFields.DietaryAssessmentDetail2
                    )}
                    type="number"
                    placeholder="예) 3, 4"
                    className={styles.dietary_input}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      !dietaryAssessmentActionWatch?.includes(
                        DietaryAssessment.dietary2
                      )
                    }
                  />
                  <span>회 /day</span>
                </div>
                <div className="flex items_center justify_between">
                  <FormCheckbox
                    name={
                      MethodsOfOralHygieneChartFields.DietaryAssessmentAction
                    }
                    text={DietaryAssessmentLabel[DietaryAssessment.dietary3]}
                    value={DietaryAssessment.dietary3}
                    multiple={true}
                    readOnly={!isRegister || isReadOnly}
                    afterChange={handleDietaryChange}
                  />
                  <input
                    {...register(
                      MethodsOfOralHygieneChartFields.DietaryAssessmentDetail3
                    )}
                    type="number"
                    placeholder="예) 3, 4"
                    className={styles.dietary_input}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      !dietaryAssessmentActionWatch?.includes(
                        DietaryAssessment.dietary3
                      )
                    }
                  />
                  <span>회 /day</span>
                </div>
              </div>
            </td>
            <td
              className={classNames(
                styles.textarea_td,
                styles.rounded_bottom_right
              )}
            >
              <FormAutoHeightTextarea
                name={MethodsOfOralHygieneChartFields.DietaryAssessmentPlan}
                textareaStyle={{
                  width: '100%',
                  padding: '16px',
                  height: '140px',
                  margin: '0',
                  borderBottom: '0',
                }}
                placeholder="구강보건교육 계획을 입력해 주세요."
                readOnly={!isRegister || isReadOnly}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default MethodsOfOralHygieneChart;
